import type { SelectedLogo } from '@/components/logo-builder/logo-builder-state';
import type { SiteLogo } from '@/types/shared';

export type LogoDraftSettings =
  | { source: 'template'; text: string; assetId: string; templateId: string }
  | { source: 'asset' | 'ai'; text: string; assetId: string };

/** Metadata-only logo settings for undo/redo draft sync (PATCH /sites/design). */
export function buildLogoDraftSettings(logo: SiteLogo | null): LogoDraftSettings | null {
  if (!logo?.source || !logo.text?.trim()) return null;

  if (logo.source === 'template') {
    if (!logo.templateId || !logo.assetId) return null;

    return {
      text: logo.text.trim(),
      source: 'template',
      templateId: logo.templateId,
      assetId: logo.assetId,
    };
  }

  if (logo.source === 'asset' || logo.source === 'ai') {
    return logo.assetId
      ? {
          text: logo.text.trim(),
          source: logo.source,
          assetId: logo.assetId,
        }
      : null;
  }

  return null;
}

/** Side-effecting logo apply payload for Logo Builder (PATCH /sites/logo). */
export function buildLogoApplyPayload(
  selectedLogo: SelectedLogo,
  logoText: string,
  renderedSvg?: string,
) {
  const trimmedLogoText = logoText.trim();

  switch (selectedLogo.source) {
    case 'template': {
      const svg = renderedSvg?.trim();
      if (!svg) {
        throw new Error('renderedSvg is required for template logo apply');
      }

      return {
        source: 'template' as const,
        templateId: selectedLogo.templateId,
        logoText: trimmedLogoText,
        renderedSvg: svg,
      };
    }
    case 'asset':
      return {
        source: 'asset' as const,
        assetId: selectedLogo.assetId,
        logoText: trimmedLogoText,
      };
    case 'ai':
      return {
        source: 'ai' as const,
        assetId: selectedLogo.assetId,
        logoText: trimmedLogoText,
      };
  }
}
